/**
 * Page settings (filters, view mode, month, search) kept in the URL instead of `useState`.
 *
 * WHY. Component state dies when the user leaves the page. Opening an OKR or a user and
 * pressing Back — the app's button OR the browser's — then showed the page reset to its
 * defaults. The URL is the only state the browser restores on Back and on reload, and it is
 * what `useOpenWithReturn` records as the place to return to.
 *
 * Contract:
 * - Values are strings. A page encodes lists with `,` and validates enums through `allowed`.
 * - A value equal to its default is removed from the URL, so untouched pages keep clean URLs.
 * - Keys the caller does not name are left alone, so pages with tabs can give each tab its own
 *   keys without one tab wiping another's.
 * - Updates use `replace`: changing a filter must not add a Back step of its own.
 * - Several updates in the same tick all land. react-router's functional `setSearchParams`
 *   hands every call the params of the last RENDER, so two calls in one event would lose the
 *   first; a ref carries the newest params between them.
 * - A step relative to the current value ("previous month") must pass a FUNCTION, which is
 *   given the newest state. Computing it from the rendered value drops clicks that arrive
 *   before the URL change has re-rendered (seen on staging: 8 fast clicks moved 6 months).
 *
 * Import from this file directly, not from the `hooks` barrel (see `useReturnNavigation.ts`).
 */

import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export type UrlStateDefaults = Record<string, string>;
export type UrlStateAllowed<D extends UrlStateDefaults> = Partial<Record<keyof D, readonly string[]>>;

/** Pure: read `defaults`' keys from `params`, falling back when missing or not allowed. */
export function readUrlState<D extends UrlStateDefaults>(
  params: URLSearchParams,
  defaults: D,
  allowed: UrlStateAllowed<D> = {}
): D {
  const out = { ...defaults };
  (Object.keys(defaults) as Array<keyof D & string>).forEach((key) => {
    const raw = params.get(key);
    if (raw === null) return;
    const ok = allowed[key];
    if (ok && !ok.includes(raw)) return;
    (out as Record<string, string>)[key] = raw;
  });
  return out;
}

/** Pure: apply `patch`; a value equal to its default (or empty) removes the key. */
export function writeUrlState<D extends UrlStateDefaults>(
  params: URLSearchParams,
  defaults: D,
  patch: Partial<D>
): URLSearchParams {
  const next = new URLSearchParams(params);
  (Object.keys(patch) as Array<keyof D & string>).forEach((key) => {
    const value = patch[key];
    if (value === undefined || value === '' || value === defaults[key]) next.delete(key);
    else next.set(key, value);
  });
  return next;
}

export function useUrlState<D extends UrlStateDefaults>(
  defaults: D,
  allowed?: UrlStateAllowed<D>
): [D, (patch: Partial<D> | ((current: D) => Partial<D>)) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const latest = useRef(searchParams);
  latest.current = searchParams;

  // Callers pass object literals; key on content so the returned values stay stable.
  const defaultsKey = JSON.stringify(defaults);
  const allowedKey = JSON.stringify(allowed ?? {});
  const stableDefaults = useMemo(() => defaults, [defaultsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const stableAllowed = useMemo(() => allowed, [allowedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const state = useMemo(
    () => readUrlState(searchParams, stableDefaults, stableAllowed),
    [searchParams, stableDefaults, stableAllowed]
  );

  const update = useCallback(
    (patch: Partial<D> | ((current: D) => Partial<D>)) => {
      const resolved =
        typeof patch === 'function' ? patch(readUrlState(latest.current, stableDefaults, stableAllowed)) : patch;
      const next = writeUrlState(latest.current, stableDefaults, resolved);
      latest.current = next;
      setSearchParams(next, { replace: true });
    },
    [setSearchParams, stableDefaults, stableAllowed]
  );

  return [state, update];
}
